import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type {
  DataTableColumn,
  DataTableColumnUpdateRequest,
  DataTableType,
} from '@/components/UI/DataTable/DataTable.model';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

interface EditDataTableMeasObsColumnForm {
  referenceDate: string;
}

interface EditDatatableMeasObsColumnProps {
  tableType: DataTableType;
  column: DataTableColumn;
  columns: Array<DataTableColumn>;
  formData: EditDataTableMeasObsColumnForm;
  closeModal: () => void;
  onSave: (columnUpdate: DataTableColumnUpdateRequest) => Promise<void>;
}

export const EditDatatableMeasObsColumn: FC<EditDatatableMeasObsColumnProps> = ({
  column,
  formData,
  onSave,
  closeModal,
}) => {
  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    control,
  } = useForm<EditDataTableMeasObsColumnForm>({
    mode: 'onChange',
    defaultValues: { ...formData },
  });

  const onSubmit = async (update: EditDataTableMeasObsColumnForm) => {
    closeModal();
    await onSave({
      id: column.id,
      type: column.type,
      measurement: { measured_at: update.referenceDate },
    } as DataTableColumnUpdateRequest);
  };

  return (
    <ModalContainer size="xnarrow">
      <ModalHeader title="Edit column" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="ph3 pv2 mt2">
          <label htmlFor="referenceDate">Date</label>
          <Controller
            control={control}
            name="referenceDate"
            render={({ field: { value, onChange } }) => (
              <DatePickerNative
                id="referenceDate"
                value={value ?? ''}
                step="any"
                max={DateUtils.dateNow()}
                onChange={onChange}
              />
            )}
          />
        </div>
      </form>
      <ModalActions
        className="pa3 mt3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitButtonProps={{ disabled: !isValid || !isDirty || isSubmitting }}
      />
    </ModalContainer>
  );
};
